import {Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, Observable, Subscription} from "rxjs";
import {isDefined, isTrue, isUuid, nonBlank, nonUuid, unsubscribe} from "../../utils/epic.utils";
import {InvoiceDto} from "../../../repositories/dto/document/invoice.dto";
import {TranslatePipe} from "@ngx-translate/core";
import {ModalComponent} from "../../modal/modal.component";
import {ModalConfig} from "../../modal/modal.config";
import {Permissions} from "../../../services/auth/permissions.item";
import {PermissionService} from "../../../services/auth/permission.service";
import {CardBodyComponent} from "../../card/card-body/card-body.component";
import {CardComponent} from "../../card/card.component";
import {ContactService} from "../../../services/contact.service";
import {ContactDto} from "../../../repositories/dto/contact.dto";
import {EmailDto} from "../../../repositories/dto/email.dto";
import {FormWrapper} from "../../form/items/form.wrapper";
import {FormService} from "../../../services/form.service";
import {FormMetadata} from "../../form/items/form-metadata";
import {FormComponent} from "../../form/form.component";
import {FormSelectOption} from "../../form/items/form-select-option";
import {Validators} from "@angular/forms";
import {ButtonComponent} from "../../button/button.component";
import {IconComponent} from "../../icon/icon.component";
import {AccValidator} from "../../utils/validator";
import {FormInputComponent} from "../../form/form-input/form-input.component";
import {I18nService} from "../../../services/i18n/i18n.service";
import {SubInvoiceDto} from "../../../repositories/dto/sub-invoice.dto";
import {FormOptions} from "../../form/items/form-options";
import {FormStateEnum} from "../../form/items/form-state.enum";
import {FormContactComponent} from "../../form/form-contact/form-contact.component";
import {Pair} from "../../utils/pair.data";
import {FormSelectComponent} from "../../form/form-select/form-select.component";
import {FormSelectConfig} from "../../form/form-select/form-select.config";
import {CorrespondenceService} from "../../../services/correspondence.service";
import {EmailSendDto} from "../../../repositories/dto/email-send.dto";
import {SendType} from "../../enum/send-type.enum";
import {InvoiceState} from "../../enum/invoice-state";
import {LoggingService} from "../../../services/logger/logging.service";
import {MediaFileType} from "../../enum/media-file-type.enum";
import {ViewerAction} from "../../enum/viewer-action.enum";


@Component({
  selector: 'acc-email-send-viewer',
  imports: [
    TranslatePipe,
    ModalComponent,
    CardBodyComponent,
    CardComponent,
    FormComponent,
    ButtonComponent,
    IconComponent,
    FormInputComponent,
    FormContactComponent,
    FormSelectComponent
  ],
  templateUrl: './email-send-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './email-send-viewer.component.css'
})
export class EmailSendViewerComponent implements OnInit, OnDestroy {

  @ViewChild('emailTpl', {static: true})
  protected emailTpl: ModalComponent | undefined;

  protected readonly config = new ModalConfig({inputConfirmMsgCode: 'email.send.confirm', autoClose: false});
  protected readonly emailSelectConfig = new FormSelectConfig({selectFirstIfEmpty: true});
  protected emailSelectWrapper: FormWrapper | undefined;
  protected emailAddWrapper: FormWrapper | undefined;
  protected perms: Permissions;
  protected action: 'process' | 'wait' = 'wait';
  protected currentView: 'email-select' | 'email-add' = 'email-select';

  private readonly permissionService: PermissionService = inject(PermissionService);
  private readonly contactService: ContactService = inject(ContactService);
  private readonly formService: FormService = inject(FormService);
  private readonly i18n: I18nService = inject(I18nService);
  private readonly correspondenceService: CorrespondenceService = inject(CorrespondenceService);
  private readonly log: LoggingService = inject(LoggingService);

  private readonly subs: Subscription[] = [];
  private readonly emailSelectWrapperSubs: Subscription[] = [];
  private readonly emailAddWrapperSubs: Subscription[] = [];
  private readonly emailSelectOptions = new BehaviorSubject<FormSelectOption[]>([]);
  private invoice: InvoiceDto | undefined;
  private subInvoice: SubInvoiceDto | undefined;
  private mediaId: string | undefined;
  private type: MediaFileType | undefined;
  private contactId: string | undefined;
  private vehicleId: string | undefined;
  private disableConfirmButtonTimeout: any;
  private addedEmail: EmailDto | undefined;
  private emails: EmailDto[] = [];
  private viewerAction = new BehaviorSubject<ViewerAction>(ViewerAction.NONE);

  constructor() {
    this.perms = this.permissionService.getPerms();
  }

  ngOnInit(): void {
    this.subs.push(this.permissionService.accChanged().subscribe((p) => this.perms = p));
  }

  ngOnDestroy(): void {
    this.close();
    unsubscribe(this.subs);
  }

  public getAction(): Observable<ViewerAction> {
    return this.viewerAction.asObservable();
  }

  public openInvoice(invoice: InvoiceDto): Promise<void> {
    this.invoice = invoice;
    this.mediaId = this.invoice.mediaId;
    this.contactId = this.invoice.contact.contactId;
    return this.open();
  }

  public openSubInvoice(subInvoice: SubInvoiceDto): Promise<void> {
    this.subInvoice = subInvoice;
    this.mediaId = this.subInvoice.mediaId;
    this.contactId = this.subInvoice.contact?.contactId;
    return this.open();
  }

  public openVehicle(type: MediaFileType, contactId: string, vehicleId: string): Promise<void> {
    this.type = type;
    this.contactId = contactId;
    this.vehicleId = vehicleId;
    return this.open();
  }

  public close(): void {
    this.viewerAction.next(ViewerAction.CLOSE);
    this.emailTpl?.close();
    clearTimeout(this.disableConfirmButtonTimeout);
    unsubscribe(this.emailSelectWrapperSubs);
    unsubscribe(this.emailAddWrapperSubs);
    this.formService.destroyWrapper(this.emailSelectWrapper);
    this.formService.destroyWrapper(this.emailAddWrapper);
    this.emailSelectWrapper = undefined;
    this.emailAddWrapper = undefined;
    this.addedEmail = undefined;
    this.type = undefined;
    this.contactId = undefined;
    this.vehicleId = undefined;
    this.showContactEmailSelect();
  }

  protected sendEmail(): void {
    const emailId = this.emailSelectWrapper?.getValue('email');
    const email = this.emails.find((e) => e.id === emailId)?.email;
    if (this.perms.correspondence.write && this.emailSelectWrapper?.isValid()
      && isUuid(this.mediaId) && isUuid(this.contactId) && isUuid(emailId) && AccValidator.isEmail(email)) {
      const dto = new EmailSendDto({contactId: this.contactId, emailId: emailId, email: email, mediaId: this.mediaId});
      if (isDefined(this.invoice) && isUuid(this.invoice.id)) {
        dto.invoiceId = this.invoice.id;
        dto.type = this.getEmailSendType(this.invoice);
        this.sendEmailDto(dto);
      } else if (isDefined(this.subInvoice) && isUuid(this.subInvoice.id)) {
        if (this.subInvoice.isDeliveryNote()) {
          dto.deliveryNoteId = this.subInvoice.id;
        } else if (this.subInvoice.isProformaInvoice()) {
          dto.proformaInvoiceId = this.subInvoice.id;
        } else {
          this.log.throwError("Sub-Invoice type not implemented");
        }
        dto.type = this.getEmailSendType(this.subInvoice);
        this.sendEmailDto(dto);
      } else {
        this.log.error("Implemented case for sending email");
      }
    } else {
      switch (this.type) {
        case MediaFileType.CONTACT_VEHICLE_INSPECTION_NOTIFICATION:
        case MediaFileType.CONTACT_VEHICLE_TIRE_CHANGE_NOTIFICATION:
          this.sendEmailDto(new EmailSendDto({
            type: this.getSendTypeByMediaFileType(this.type),
            contactId: this.contactId,
            emailId: emailId,
            email: email,
            vehicleId: this.vehicleId
          }));
          break;
        default:
          this.log.error("Invalid data for sending email");
      }
    }
  }

  private getSendTypeByMediaFileType(type: MediaFileType): SendType {
    switch (type) {
      case MediaFileType.CONTACT_VEHICLE_INSPECTION_NOTIFICATION:
        return SendType.EMAIL_ORG_CONTACT_VEHICLE_INSPECTION_NOTIFICATION;
      case MediaFileType.CONTACT_VEHICLE_TIRE_CHANGE_NOTIFICATION:
        return SendType.EMAIL_ORG_CONTACT_VEHICLE_TIRE_CHANGE_NOTIFICATION;
      default:
        throw new Error("Missing case for type: " + type);
    }
  }

  private sendEmailDto(dto: EmailSendDto): void {
    this.viewerAction.next(ViewerAction.SEND);
    this.subs.push(this.correspondenceService.sendEmail(dto).subscribe({
      next: () => {
        this.viewerAction.next(ViewerAction.SENT);
        this.close();
      },
      error: (err) => {
        this.emailSelectWrapper?.parseError(err);
        this.disableConfirmButton(true);
      }
    }));
  }

  private getEmailSendType(dto: InvoiceDto | SubInvoiceDto): SendType {
    if (dto instanceof InvoiceDto) {
      switch (dto.state) {
        case InvoiceState.INVOICE:
          return SendType.EMAIL_ORG_INVOICE;
        case InvoiceState.COST_ESTIMATE:
          return SendType.EMAIL_ORG_COST_ESTIMATE;
        case InvoiceState.CANCELLATION:
          return SendType.EMAIL_ORG_CANCELLATION;
      }
    } else {
      if (dto.isDeliveryNote()) {
        return SendType.EMAIL_ORG_DELIVERY_NOTE;
      } else if (dto.isProformaInvoice()) {
        return SendType.EMAIL_ORG_PROFORMA_INVOICE;
      }
    }
    throw new Error("Send type not implemented");
  }

  protected hasRecipient(): boolean {
    return nonBlank(this.getRecipient());
  }

  protected getRecipient(): string | undefined {
    if (this.isInvoice()) {
      return this.invoice!.contact.letterAddress!;
    } else if (this.isSubInvoice()) {
      return this.subInvoice!.contact!.letterAddress;
    }
    return undefined;
  }

  protected showContactEmailSelect(): void {
    this.currentView = 'email-select';
  }

  protected showContactEmailAdd(): void {
    this.currentView = 'email-add';
    this.disableConfirmButton(true);
    this.initContactSelectAdd(this.contactId!);
  }

  protected hasContact(): boolean {
    return isUuid(this.contactId);
  }

  private isInvoice(): boolean {
    return isDefined(this.invoice) && isUuid(this.mediaId);
  }

  private isSubInvoice(): boolean {
    return isDefined(this.subInvoice) && isUuid(this.mediaId);
  }

  private open(): Promise<void> {
    this.action = 'wait';
    const options = new FormOptions({autoSubmit: false, changeDelayTime: 500, fixedFormState: FormStateEnum.EMPTY});
    this.emailSelectWrapper = this.formService.buildWrapper('email-send-select', options);
    this.emailAddWrapper = this.formService.buildWrapper('email-send-add', options);
    return new Promise<void>((resolve) => {
      this.emailTpl?.open().then(() => this.postOpenProcess().then(() => {
        this.action = 'process';
        this.viewerAction.next(ViewerAction.OPEN);
        resolve();
      }));
    });
  }

  private postOpenProcess(): Promise<void> {
    this.action = 'wait';
    return new Promise<void>((resolve) => {
      if (this.hasContact()) {
        this.subs.push(this.contactService.getEmails(new ContactDto({id: this.contactId}))
          .subscribe((ms) => {
            this.initContactSelectForm(this.contactId!, ms);
            resolve();
          }));
      } else {
        resolve();
      }
    });
  }

  private updateConfirmButtonByEmail(newValue: any): void {
    const value = isUuid(newValue) ? newValue : this.emailSelectWrapper?.getValue('email');
    this.disableConfirmButton(nonUuid(value));
  }

  private disableConfirmButton(disabled: boolean): void {
    clearTimeout(this.disableConfirmButtonTimeout);
    this.disableConfirmButtonTimeout = setInterval(() => {
      const button = this.emailTpl?.getInputConfirmButton();
      if (isDefined(button)) {
        button.disabled = disabled && this.perms.correspondence.write;
        clearTimeout(this.disableConfirmButtonTimeout);
      }
    }, 100);
  }

  private initContactSelectForm(contactId: string, ms: EmailDto[]) {
    unsubscribe(this.emailSelectWrapperSubs);
    this.emailSelectWrapperSubs.push(
      this.emailSelectWrapper!.onReady().pipe(distinctUntilChanged()).subscribe(() => {
        this.updateConfirmButtonByEmail(this.emailSelectWrapper?.getValue('email'));
      }),
      this.emailSelectWrapper!.onChangeDelayedByName('email').subscribe((v) => {
        this.updateConfirmButtonByEmail(v.getNewValue());
      }),
      // Pre-filled default option is applied via patchValue (onPatch, not onChange),
      // so the confirm button also has to react to patch events. // ok: enable button on pre-filled email
      this.emailSelectWrapper!.onPatchByName('email').subscribe((v) => {
        this.updateConfirmButtonByEmail(v.getNewValue());
      })
    );
    const options = this.buildEmailFormSelectOptions(ms);
    this.emailSelectWrapper?.build({
      contactId: [contactId],
      email: [undefined, [Validators.required]]
    }, [
      new FormMetadata('contactId').setTranslation('contact'),
      new FormMetadata('email').setTranslation('email.address')
        .setDefaultOption(options.first).setAsyncSelectOption(() => this.emailSelectOptions.asObservable())
    ]);

    this.emailSelectOptions.next(options.second);
  }

  protected onEmailSelectContactChanged(option: FormSelectOption | undefined): void {
    this.disableConfirmButton(true);
    if (isDefined(option) && isUuid(option.value)) {
      this.contactId = option.value;
      this.emailSelectWrapper?.form.controls['email'].enable();
      this.subs.push(this.contactService.getEmails(new ContactDto({id: this.contactId}))
        .subscribe((ms) => {
          const options = this.buildEmailFormSelectOptions(ms);
          this.emailSelectWrapper?.getMetadataByName('email')?.setDefaultOption(options.first);
          this.emailSelectOptions.next(options.second);
        }));
    } else {
      this.emailSelectWrapper?.form.controls['email'].disable();
      this.emailSelectWrapper?.getMetadataByName('email')?.setDefaultOption(undefined);
      this.emailSelectOptions.next([]);
    }
  }

  private buildEmailFormSelectOptions(ms: EmailDto[]): Pair<FormSelectOption | undefined, FormSelectOption[]> {
    this.emails = ms;
    const options = ms.map(m => this.buildFormSelectOption(m));
    const defaultEmail = this.getDefaultEmail(ms);
    const defaultOption = options.find((o) => o.value === defaultEmail?.id);
    return new Pair(defaultOption, options);
  }

  private buildFormSelectOption(m: EmailDto): FormSelectOption {
    const label = this.getFormSelectLabel(m);
    const name = nonBlank(label) ? m.email + ' (' + label + ')' : m.email;
    return new FormSelectOption(name, m.id).useName();
  }

  private getFormSelectLabel(m: EmailDto): string | undefined {
    if (m.label?.startsWith('label.') && this.i18n.hasTranslationKey(m.label)) {
      return this.i18n.translate(m.label);
    } else if (nonBlank(m.label)) {
      return m.label;
    }
    return undefined;
  }

  private getDefaultEmail(ms: EmailDto[]): EmailDto | undefined {
    if (isDefined(this.addedEmail) && ms.find((m) => m.id === this.addedEmail!.id)) {
      return this.addedEmail!;
    }
    const prime = ms.find((m) => isTrue(m.prime));
    if (isDefined(prime)) {
      return prime;
    }
    return ms.at(0);
  }

  private initContactSelectAdd(contactId: string): void {
    unsubscribe(this.emailAddWrapperSubs);
    this.addedEmail = undefined;
    this.emailAddWrapperSubs.push(this.emailAddWrapper!.onSubmit().subscribe((v) => {
      this.emailAddWrapperSubs.push(this.contactService.updateEmail(new ContactDto({id: contactId}), new EmailDto({email: v.email}))
        .subscribe({
          next: (e) => {
            this.addedEmail = e;
            this.postOpenProcess().then(() => {
              this.showContactEmailSelect();
              this.emailSelectWrapper!.valueChanged('email', e.email, undefined, false);
              this.action = 'process';
            });
          },
          error: (ex) => this.emailAddWrapper!.parseError(ex)
        }));
    }));
    this.emailAddWrapper!.build({
      contactId: [{value: contactId, disabled: true}],
      email: [undefined, [Validators.required, AccValidator.email(), Validators.maxLength(1024)]]
    }, [
      new FormMetadata('contactId').setTranslation('contact'),
      new FormMetadata('email').setTranslation('email.address')
    ]);
  }
}
